import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import apiClient from '../api/axios';
import { toast } from 'react-toastify';

// Assuming UserOrgRole enum values are available or can be hardcoded
// For simplicity, let's hardcode them for now, or fetch from backend if an endpoint exists
const userOrgRoles = [
    { value: 'owner', label: 'Владелец' },
    { value: 'admin', label: 'Администратор' },
    { value: 'editor', label: 'Редактор' },
    { value: 'member', label: 'Участник' },
];

const CreateUserModal = ({ show, handleClose, onUserCreated }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [isEmailConfirmed, setIsEmailConfirmed] = useState(false);
    const [organizationId, setOrganizationId] = useState(''); // New state for organization
    const [roleInOrganization, setRoleInOrganization] = useState(''); // New state for role

    const [organizations, setOrganizations] = useState([]); // State to store organizations
    const [loadingOrgs, setLoadingOrgs] = useState(true);
    const [orgsError, setOrgsError] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                const response = await apiClient.get('/admin/organizations/');
                setOrganizations(response.data.organizations);
            } catch (err) {
                setOrgsError('Не удалось загрузить список организаций.');
                console.error('Error fetching organizations:', err);
            } finally {
                setLoadingOrgs(false);
            }
        };

        if (show) { // Fetch organizations only when modal is shown
            fetchOrganizations();
        }
    }, [show]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                email,
                password,
                name: name || null,
                is_email_confirmed: isEmailConfirmed,
            };

            if (organizationId) { // Only add if an organization is selected
                payload.organization_id = parseInt(organizationId);
                payload.role_in_organization = roleInOrganization || 'member'; // Default to member if org selected but no role
            }

            const response = await apiClient.post('/admin/users/', payload); // Use new admin endpoint
            toast.success('Пользователь успешно создан!');
            onUserCreated();
            handleClose();
            // Reset form fields
            setEmail('');
            setPassword('');
            setName('');
            setIsEmailConfirmed(false);
            setOrganizationId('');
            setRoleInOrganization('');
        } catch (err) {
            let errorMessage = 'Ошибка при создании пользователя.';
            if (err.response && err.response.data && err.response.data.detail) {
                const detail = err.response.data.detail;
                if (Array.isArray(detail)) {
                    // Handle FastAPI validation errors (multiple fields)
                    errorMessage = detail.map(e => e.msg || 'Неизвестная ошибка валидации').join('; ');
                } else if (typeof detail === 'object' && detail !== null && detail.msg) {
                    // Handle single validation error
                    errorMessage = detail.msg;
                } else if (typeof detail === 'string') {
                    // Handle simple string error
                    errorMessage = detail;
                }
            }
            setError(errorMessage);
            toast.error(errorMessage);
            console.error('Create user error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Создать нового пользователя</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {orgsError && <Alert variant="warning">{orgsError}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3" controlId="formBasicEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="Введите email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formBasicPassword">
                        <Form.Label>Пароль</Form.Label>
                        <Form.Control
                            type="password"
                            placeholder="Введите пароль"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formBasicName">
                        <Form.Label>Имя (необязательно)</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Введите имя"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formBasicEmailConfirmed">
                        <Form.Check
                            type="checkbox"
                            label="Email подтвержден"
                            checked={isEmailConfirmed}
                            onChange={(e) => setIsEmailConfirmed(e.target.checked)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formOrganization">
                        <Form.Label>Организация (необязательно)</Form.Label>
                        <Form.Control
                            as="select"
                            value={organizationId}
                            onChange={(e) => {
                                setOrganizationId(e.target.value);
                                if (!e.target.value) { // Reset role if no organization selected
                                    setRoleInOrganization('');
                                }
                            }}
                            disabled={loadingOrgs}
                        >
                            <option value="">Выберите организацию</option>
                            {organizations.map(org => (
                                <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>

                    {organizationId && ( // Only show role dropdown if an organization is selected
                        <Form.Group className="mb-3" controlId="formRoleInOrganization">
                            <Form.Label>Роль в организации</Form.Label>
                            <Form.Control
                                as="select"
                                value={roleInOrganization}
                                onChange={(e) => setRoleInOrganization(e.target.value)}
                                required={!!organizationId} // Required if organization is selected
                            >
                                <option value="">Выберите роль</option>
                                {userOrgRoles.map(role => (
                                    <option key={role.value} value={role.value}>{role.label}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    )}

                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? 'Создание...' : 'Создать'}
                    </Button>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default CreateUserModal;